import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  Extrapolation,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import type { MenuFlowSection } from '../../../data/domains/foodMenu';
import { MenuFlowSectionHeader, SECTION_HEADER_HEIGHT } from '../../components/MenuFlowSectionHeader';

const HANDOFF_MS = 220;
const HANDOFF_TRAVEL = SECTION_HEADER_HEIGHT;
const HANDOFF_EASING = Easing.out(Easing.cubic);

type Props = {
  currentSection: MenuFlowSection | null;
  /** Stable id for food-type changes (index or foodTypeId). Count-only updates skip the slide. */
  sectionKey: string | number;
  header_collapsed?: boolean;
};

/**
 * set2 sticky section chip: vertical handoff that reads like the list bar docking into the header.
 * Count uses the same chip tag as in-list section bars.
 */
function MenuFlowSectionHandoffSet2Component({
  currentSection,
  sectionKey,
  header_collapsed = false,
}: Props) {
  const progress = useSharedValue(1);
  const [outgoing, setOutgoing] = useState<MenuFlowSection | null>(currentSection);
  const [incoming, setIncoming] = useState<MenuFlowSection | null>(null);
  const key_ref = useRef(sectionKey);
  const outgoing_ref = useRef(currentSection);
  const incoming_ref = useRef<MenuFlowSection | null>(null);

  const settle = useCallback((next: MenuFlowSection | null) => {
    setOutgoing(next);
    outgoing_ref.current = next;
    setIncoming(null);
    incoming_ref.current = null;
    progress.value = 1;
  }, [progress]);

  useEffect(() => {
    if (sectionKey === key_ref.current) {
      if (incoming_ref.current) {
        setIncoming(currentSection);
        incoming_ref.current = currentSection;
      } else {
        setOutgoing(currentSection);
        outgoing_ref.current = currentSection;
      }
      return;
    }

    const from = incoming_ref.current ?? outgoing_ref.current;
    key_ref.current = sectionKey;
    setOutgoing(from);
    outgoing_ref.current = from;
    setIncoming(currentSection);
    incoming_ref.current = currentSection;

    cancelAnimation(progress);
    progress.value = 0;
    progress.value = withTiming(1, { duration: HANDOFF_MS, easing: HANDOFF_EASING }, finished => {
      if (finished) {
        runOnJS(settle)(currentSection);
      }
    });
  }, [sectionKey, currentSection, progress, settle]);

  const outgoing_style = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 0.55, 1], [1, 0.25, 0], Extrapolation.CLAMP),
    transform: [
      {
        translateY: interpolate(progress.value, [0, 1], [0, -HANDOFF_TRAVEL], Extrapolation.CLAMP),
      },
    ],
  }));

  const incoming_style = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 0.3, 1], [0, 0.7, 1], Extrapolation.CLAMP),
    transform: [
      {
        translateY: interpolate(progress.value, [0, 1], [HANDOFF_TRAVEL, 0], Extrapolation.CLAMP),
      },
    ],
  }));

  const slot_style = useMemo(
    () => ({ height: SECTION_HEADER_HEIGHT, width: '100%' as const, overflow: 'hidden' as const }),
    [],
  );

  // Settled: static chip header (handoff opacity curve ends at 0 — must not stay applied).
  if (!incoming) {
    return (
      <View style={slot_style} pointerEvents="box-none">
        <MenuFlowSectionHeader
          currentSection={outgoing}
          chrome_variant="set2"
          header_collapsed={header_collapsed}
          count_chrome="chip"
        />
      </View>
    );
  }

  return (
    <View style={slot_style} pointerEvents="box-none">
      <Animated.View style={[styles.layer, outgoing_style]} pointerEvents="none">
        <MenuFlowSectionHeader
          currentSection={outgoing}
          chrome_variant="set2"
          header_collapsed={header_collapsed}
          count_chrome="chip"
        />
      </Animated.View>
      <Animated.View style={[styles.layer, incoming_style]} pointerEvents="none">
        <MenuFlowSectionHeader
          currentSection={incoming}
          chrome_variant="set2"
          header_collapsed={header_collapsed}
          count_chrome="chip"
        />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  layer: {
    ...StyleSheet.absoluteFillObject,
  },
});

export const MenuFlowSectionHandoffSet2 = memo(MenuFlowSectionHandoffSet2Component);
